import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const DataVisualization = ({
  data = [],
  chartType = 'bar',
  xAxisField,
  yAxisField,
  title = 'Chart',
  height = 400
}) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0 || !xAxisField || !yAxisField) {
      return null;
    }

    // Prepare data for Chart.js
    const labels = data.map(item => item[xAxisField] || 'N/A');
    const values = data.map(item => {
      const value = item[yAxisField];
      return typeof value === 'number' ? value : parseFloat(value) || 0;
    });

    return {
      labels,
      datasets: [{
        label: yAxisField.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
        data: values,
        backgroundColor: chartType === 'pie'
          ? [
              '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
              '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
            ]
          : 'rgba(54, 162, 235, 0.6)',
        borderColor: chartType === 'pie'
          ? [
              '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
              '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
            ]
          : 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      }],
    };
  }, [data, chartType, xAxisField, yAxisField]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: title,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat().format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: chartType !== 'pie' ? {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return new Intl.NumberFormat().format(value);
          }
        }
      }
    } : {}
  }), [chartType, title]);

  if (!chartData) {
    return (
      <div style={{
        height: height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        color: '#999'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <div>Please select X and Y axis fields to display chart</div>
        </div>
      </div>
    );
  }

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return <Bar data={chartData} options={options} />;
      case 'line':
        return <Line data={chartData} options={options} />;
      case 'pie':
        return <Pie data={chartData} options={options} />;
      default:
        return <Bar data={chartData} options={options} />;
    }
  };

  return (
    <div style={{ height: height, width: '100%' }}>
      {renderChart()}
    </div>
  );
};

export default DataVisualization;